import { Controller } from "@hotwired/stimulus"
import { get as getFetch } from "@lib/requests"

export default class extends Controller {

  static values = { resource: String, togglePath: String }
  
  static classes = ["selected"]
  
  connect() {
    // this.actionBox = this.element.querySelector("#action-box")
    this.hasPreviousSelection = false
    this.checkboxes = Array.from(this.element.querySelectorAll("tbody [type='checkbox']"))
    this.toggler = this.element.querySelector("thead [type='checkbox']")
    this.setDefaults()    
    this.checkInitial()
    this.storeInitialData()
    this.handleEscape = this.handleEscape.bind(this)
    document.addEventListener("keydown", this.handleEscape)
  }

  disconnect() {
    document.removeEventListener("keydown", this.handleEscape)
  }
  
  setDefaults() {
    this.element.setAttribute("data-table-selected-class", "selected bg-yellow-100/70")
    this.checkboxes.forEach(cb => cb.setAttribute("data-action", "click->table#toggle"))
    this.toggler?.setAttribute("data-action", "change->table#toggleAll")
  }

  storeInitialData() {
    const element = document.querySelector("#table-data")
    if (element) { this.initialData = element.innerHTML }
  }

  reinstateInitialData() {
    const element = document.querySelector("#table-data")
    element.innerHTML = this.initialData || ""
  }

  handleEscape(event) {
    if (event.key !== "Escape") return
    if (!this.hasSelection) return
    this.deselectAll()
  }
  
  get checked() {
    return this.checkboxes.filter(checkbox => checkbox.checked)
  }

  get ids() {
    return this.checked.map(checkbox => checkbox.closest("tr").dataset.id)
  }

  get resourcesQueryString() {
    if (!this.hasResourceValue) return ""
    const resourcesString = this.ids.map(id => `${this.resourceValue}_${id}`).join(",")
    return `resources=${resourcesString}`
  }

  get noneSelected() {
    return this.checked.length === 0
  }

  get partSelected() {
    return this.checked.length > 0 && !this.allSelected
  }
  
  get allSelected() {
    return this.checked.length === this.checkboxes.length
  }
  
  get hasSelection() {
    return this.checked.length > 0
  }

  get checkedText() {
    if (this.noneSelected) return ""
    if (this.checked.length === 1) return "1 article"
    return `${this.checked.length} articles`
  }
  
  checkInitial() {
    this.checkboxes.forEach(checkbox => {
      if (checkbox.checked) {
        checkbox.closest("tr").classList.add(...this.selectedClasses)
      }
    })
    this.refreshParts()
  }

  updateToggler() {
    if (!this.toggler) return
    if (this.allSelected) {
      this.toggler.checked = true
      this.toggler.indeterminate = false
      this.hasPreviousSelection = true
    } else if (this.partSelected) {
      this.toggler.indeterminate = true
      this.toggler.checked = false
      this.hasPreviousSelection = true
    } else {
      this.toggler.indeterminate = false
      this.toggler.checked = false
      if (this.hasPreviousSelection) {
        this.reinstateInitialData()
        this.hasPreviousSelection = false
      }
    }
  }
  
  toggle(event) {
    const element = event.target
    const row = element.closest("tr")
    const shiftKeyPressed = event.shiftKey
    
    if (shiftKeyPressed && this.lastChecked) {
      const allRows = Array.from(this.element.querySelectorAll('tr'))
      const currentIndex = allRows.indexOf(row)
      const lastIndex = allRows.indexOf(this.lastChecked.closest('tr'))
      
      const start = Math.min(currentIndex, lastIndex)
      const end = Math.max(currentIndex, lastIndex)
      
      for (let i = start; i <= end; i++) {
        const checkbox = allRows[i].querySelector('input[type="checkbox"]')
        if (checkbox) {
          checkbox.checked = element.checked
          if (element.checked) {
            allRows[i].classList.add(...this.selectedClasses)
          } else {
            allRows[i].classList.remove(...this.selectedClasses)
          }
        }
      }
    } else {
      if (element.checked) {
        row.classList.add(...this.selectedClasses)
      } else {
        row.classList.remove(...this.selectedClasses)
      }
      this.lastChecked = element
    }
    
    this.updateToggler()
    this.refreshParts()
  }

  toggleAll(event) {
    const element = event.currentTarget
    if (this.noneSelected || this.partSelected) {
      this.selectAll()
    } else {
      this.deselectAll()
    }
    this.dispatch("toggleAll")
    this.refreshParts()
  }

  selectAll() {
    this.checkboxes.forEach(checkbox => {
      checkbox.checked = true
      checkbox.closest("tr").classList.add(...this.selectedClasses)
    })
    this.toggler.checked = true
    // this.refreshParts()
  }

  deselectAll() {
    this.checkboxes.forEach(checkbox => {
      checkbox.checked = false
      checkbox.closest("tr").classList.remove(...this.selectedClasses)
    })
    this.refreshParts()
  }

  updateExportButtons() {
    const exportButton = document.getElementById("export-button");
    const tableExportButton = document.getElementById("table-export-button");
    
    if (!exportButton && !tableExportButton) return;
  
    const url = new URL(window.location.href)
    
    // Remove .csv extension if it exists and add it back
    url.pathname = url.pathname.replace(/\.csv$/, '') + '.csv'

    // Create hidden inputs for the form submission
    if (tableExportButton) {
      const form = tableExportButton.closest('form');
      // Clear any existing hidden inputs
      form.querySelectorAll('input[name="ids[]"]').forEach(input => input.remove());
      
      // Add new hidden inputs for each selected ID
      this.ids.forEach(id => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'ids[]';
        input.value = id;
        form.appendChild(input);
      });
    }

    // For the regular export button, keep using URL parameters
    if (exportButton) {
      // Clear existing ids[] parameters
      url.searchParams.delete('ids[]');
      // Add the ids to the query parameters
      this.ids.forEach((id) => url.searchParams.append("ids[]", id));
      exportButton.href = url.href;
    }

    // Should we show a confirmation warning? (eg: if there are no search params)
    const warning = url.searchParams.size === 0 ? `Etes-vous sûr de vouloir exporter tous les éléments de la table ?` : ""
    if (exportButton) {
      exportButton.setAttribute("data-turbo-confirm", warning);
    }
  }

  toggleActionBox() {

    // Emit a custom event toggle-row with the ids of the selected rows
    const event = new CustomEvent("toggle-row", { bubbles: true, detail: { ids: this.ids } })
    window.dispatchEvent(event)

    // Return and hide the box if there are no selected items
    if (!this.hasSelection) {
      this.element.querySelector("#action-box")?.classList.add("hidden")
      document.querySelector(".table-total-button")?.classList.add("hidden")
      document.querySelector("#table-selection-total")?.remove()
      return
    }

    document.querySelector(".table-total-button")?.classList.remove("hidden")
    
    // If a toggle path has been set we will show a placeholder
    if (this.togglePathValue) {
    
      // Unhide the placeholder action box
      this.element.querySelector(".action-box-placeholder")?.classList.remove("hidden")
      
      // If there are selected items we will call for a turbo stream to show the action box
      if (this.hasSelection) {
        const url = this.togglePathValue + "?" + this.ids.map(id => `ids[]=${id}`).join("&")
        getFetch(url, { responseKind: "turbo-stream" })      
      }

    }
    
  }
  
  refreshParts() {
    this.updateExportButtons()
    this.toggleActionBox()
    this.updateToggler()
  }

  actionSelect(e) {
    // e.preventDefault()
    const loadingScreen = document.getElementById("loading-screen")
    loadingScreen?.classList.remove("hidden")
    e.currentTarget.click()
  }

}
